import { useState, useEffect, useContext } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { AppContext } from '../App';
import { FileText, TrendingUp, Calendar, Clock, FileSignature, CreditCard, AlertTriangle, MessageCircle, Building2, Bell, ExternalLink, CheckCircle, XCircle, Loader2, History, RefreshCw, ArrowUpCircle, Zap } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { formatDateUTC, daysUntilUTCDate } from '../lib/utils';

export default function Dashboard() {
  const { API_URL, membershipStatus: membership, membershipLoading, refetchMembership } = useContext(AppContext);
  const [stats, setStats] = useState({
    total_centro: 0,
    firmados_dia: 0,
    firmados_semana: 0,
    firmados_mes: 0
  });
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRenewalAlert, setShowRenewalAlert] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [processingUpgrade, setProcessingUpgrade] = useState(false);
  const [processingRenewal, setProcessingRenewal] = useState(false);
  const [paymentAction, setPaymentAction] = useState({ type: 'payment', newTypeId: null, planName: '' });

  useEffect(() => {
    fetchData();
    checkPaymentReturn();
  }, []);

  const checkPaymentReturn = async () => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment_status');
    const txnId = params.get('txn_id');

    // Legacy: redirect old payment callbacks to new page
    if (paymentStatus && txnId) {
      window.location.href = `/payment-result?gateway=${paymentStatus}&txn_id=${txnId}`;
      return;
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, companyRes] = await Promise.all([
        axios.get(`${API_URL}/statistics`, { headers }),
        axios.get(`${API_URL}/company/profile`, { headers })
      ]);

      setStats(statsRes.data);
      setCompany(companyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const res = await axios.get(`${API_URL}/payments/methods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentMethods(res.data.methods || []);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const res = await axios.get(`${API_URL}/payments/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(res.data || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const handleOpenPaymentModal = async (actionType = 'payment', newTypeId = null, planName = '') => {
    setPaymentAction({ type: actionType, newTypeId, planName });
    await fetchPaymentMethods();
    setShowPaymentModal(true);
  };

  const handleSelectPaymentMethod = async (method) => {
    if (method.id === 'manual') {
      handlePayManual();
      return;
    }

    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('konemed_token');
      const body = {
        gateway: method.id,
        action_type: paymentAction.type
      };
      if (paymentAction.newTypeId) {
        body.new_type_id = paymentAction.newTypeId;
      }

      const res = await axios.post(`${API_URL}/payments/create`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success && res.data.payment_url) {
        toast.success('Redirigiendo al portal de pago...');
        window.location.href = res.data.payment_url;
      } else {
        toast.error('Error al crear el pago');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al procesar el pago';
      toast.error(detail);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePayManual = () => {
    const message = generateWhatsAppMessage();
    const whatsappUrl = `https://wa.me/573008799545?text=${message}`;
    window.open(whatsappUrl, '_blank');
    setShowPaymentModal(false);
  };

  const generateWhatsAppMessage = () => {
    if (!company || !membership) return '';
    
    const moneda = membership.moneda || 'COP';
    const valorFormateado = new Intl.NumberFormat('es-CO').format(membership.valor_membresia || 0);
    
    const message = `Quiero pagar mi membresía:
- Razón Social: ${company.razon_social || 'N/A'}
- Documento: ${company.numero_documento || 'N/A'}
- País: ${company.pais || 'N/A'}
- Código de Empresa: ${company.codigo_empresa}
- Tipo de Membresía: ${membership.tipo_membresia || 'N/A'}
- Valor a Pagar: $${valorFormateado} ${moneda}`;
    
    return encodeURIComponent(message);
  };

  const handleRenewMembership = async () => {
    handleOpenPaymentModal('renewal');
  };

  const handleOpenUpgradeModal = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const res = await axios.get(`${API_URL}/company/available-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailablePlans(res.data.plans || []);
      setShowUpgradeModal(true);
    } catch (err) {
      toast.error('Error al cargar los planes disponibles');
    }
  };

  const handleUpgradePlan = async (planId, planName) => {
    setShowUpgradeModal(false);
    handleOpenPaymentModal('upgrade', planId, planName);
  };

  const formatCurrency = (amount, currency = 'COP') => {
    const formatted = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
    return `$${formatted} ${currency}`;
  };

  const getDaysUntilRenewal = () => {
    return daysUntilUTCDate(membership?.fecha_renovacion);
  };

  const daysUntilRenewal = getDaysUntilRenewal();
  const isNearExpiration = daysUntilRenewal !== null && daysUntilRenewal <= 5 && daysUntilRenewal >= 0;
  const isExpired = daysUntilRenewal !== null && daysUntilRenewal < 0;

  const statCards = [
    { title: 'Total Centro', value: stats.total_centro, icon: FileText, color: 'violet', testId: 'stat-total' },
    { title: 'Firmados Hoy', value: stats.firmados_dia, icon: Clock, color: 'emerald', testId: 'stat-today' },
    { title: 'Firmados Semana', value: stats.firmados_semana, icon: Calendar, color: 'cyan', testId: 'stat-week' },
    { title: 'Firmados Mes', value: stats.firmados_mes, icon: TrendingUp, color: 'indigo', testId: 'stat-month' },
  ];

  // Only treat as blocked once we have a confirmed membership response — not while it's
  // still loading or after a failed/rate-limited fetch (see App.js's refetchMembership).
  const isBlocked = !!membership && (!membership.has_membership || membership.bloqueado);

  const statusColors = {
    paid: 'bg-emerald-100 text-emerald-700',
    processing: 'bg-blue-100 text-blue-700',
    pending: 'bg-amber-100 text-amber-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-600',
    expired: 'bg-slate-100 text-slate-500'
  };

  const statusLabels = {
    paid: 'Pagado',
    processing: 'Procesando',
    pending: 'Pendiente',
    rejected: 'Rechazado',
    cancelled: 'Cancelado',
    expired: 'Expirado'
  };

  return (
    <DashboardLayout>
      <div data-testid="dashboard-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="dashboard-title">Estadísticas</h1>
          <p className="text-slate-600">Panel de control y métricas del centro</p>
        </div>

        {checkingPayment && (
          <div className="mb-8 bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <Loader2 className="animate-spin text-blue-600" size={24} />
              <span className="text-blue-700 font-semibold">Verificando estado del pago...</span>
            </div>
          </div>
        )}

        {/* Alert if blocked */}
        {isBlocked && (
          <div className="mb-8 bg-red-50 border-2 border-red-200 rounded-2xl p-6" data-testid="blocked-alert">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <AlertTriangle className="text-red-600" size={32} />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-red-900 mb-2">Membresía Inactiva</h2>
                <p className="text-red-700 mb-4">
                  {!membership?.has_membership 
                    ? 'Su centro no tiene una membresía activa. Active su membresía para utilizar todas las funciones del sistema.'
                    : 'Su membresía está bloqueada. Por favor realice el pago pendiente para continuar utilizando el sistema.'
                  }
                </p>
                <button
                  onClick={() => handleOpenPaymentModal('renewal')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg transition-colors"
                  data-testid="btn-pagar-membresia-alert"
                >
                  <CreditCard size={20} />
                  Pagar Membresía
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Alert for near expiration */}
        {!isBlocked && (isNearExpiration || isExpired) && showRenewalAlert && (
          <div className={`mb-8 border-2 rounded-2xl p-6 ${isExpired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid="renewal-alert">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-xl ${isExpired ? 'bg-red-100' : 'bg-amber-100'}`}>
                <Bell className={isExpired ? 'text-red-600' : 'text-amber-600'} size={32} />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className={`text-xl font-bold mb-2 ${isExpired ? 'text-red-900' : 'text-amber-900'}`}>
                      {isExpired 
                        ? '¡Su membresía ha vencido!' 
                        : daysUntilRenewal === 0 
                          ? '¡Su membresía vence hoy!'
                          : daysUntilRenewal === 1
                            ? '¡Su membresía vence mañana!'
                            : `¡Su membresía vence en ${daysUntilRenewal} días!`
                      }
                    </h2>
                    <p className={`mb-4 ${isExpired ? 'text-red-700' : 'text-amber-700'}`}>
                      {isExpired 
                        ? `Su membresía venció hace ${Math.abs(daysUntilRenewal)} día(s). Realice el pago para continuar utilizando el sistema sin interrupciones.`
                        : `La fecha de renovación de su membresía "${membership?.tipo_membresia}" es el ${formatDateUTC(membership?.fecha_renovacion)}. Realice el pago a tiempo para evitar interrupciones en el servicio.`
                      }
                    </p>
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        onClick={() => handleOpenPaymentModal('renewal')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg transition-colors shadow-lg shadow-emerald-200"
                        data-testid="btn-pagar-membresia-renewal"
                      >
                        <CreditCard size={20} />
                        Pagar Membresía Ahora
                      </button>
                      <div className={`px-4 py-2 rounded-lg font-semibold ${isExpired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        Valor: {formatCurrency(membership?.valor_membresia, membership?.moneda)}
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowRenewalAlert(false)}
                    className={`p-2 rounded-lg transition-colors ${isExpired ? 'hover:bg-red-100 text-red-400' : 'hover:bg-amber-100 text-amber-400'}`}
                    title="Cerrar notificación"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nueva Firma CTA */}
        <div className={`mb-8 bg-gradient-to-br from-violet-600 to-indigo-700 rounded-2xl p-8 text-white shadow-xl ${isBlocked ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold mb-3">Nueva Firma de Consentimiento</h2>
              <p className="text-violet-100 mb-6 max-w-2xl">
                Comienza creando una nueva solicitud de firma. Ingresa los datos del paciente y envía el link por correo o WhatsApp.
              </p>
              <a
                href={isBlocked ? '#' : '/nueva-firma'}
                data-testid="cta-nueva-firma-hero"
                className={`inline-flex items-center gap-2 px-8 py-4 bg-white text-violet-600 rounded-lg font-bold transition-all shadow-lg ${isBlocked ? 'cursor-not-allowed' : 'hover:bg-violet-50 active:scale-95'}`}
                onClick={(e) => isBlocked && e.preventDefault()}
              >
                <FileSignature size={24} />
                Crear Nueva Firma
              </a>
            </div>
            <div className="hidden lg:block">
              <div className="w-32 h-32 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <FileSignature size={64} className="text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Facturación Section */}
        <div className="mb-8 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="facturacion-section">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="text-white" size={24} />
                <h2 className="text-xl font-bold text-white">Facturación</h2>
              </div>
              {membership?.has_membership && (
                <button
                  onClick={async () => {
                    await fetchPaymentHistory();
                    setShowHistory(!showHistory);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
                  data-testid="btn-historial-pagos"
                >
                  <History size={16} />
                  Historial de Pagos
                </button>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : membership?.has_membership ? (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div className="bg-violet-50 rounded-xl p-4">
                  <p className="text-sm text-violet-600 mb-1">Nombre de la Membresía</p>
                  <p className="text-2xl font-bold text-violet-900" data-testid="membership-name">
                    {membership.tipo_membresia}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm text-blue-600 mb-1">Cantidad de Consentimientos</p>
                  <p className="text-2xl font-bold text-blue-900" data-testid="consent-limit">
                    {membership.cantidad_consentimientos === -1 ? 'Ilimitados' : membership.cantidad_consentimientos}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-sm text-amber-600 mb-1">Consentimientos Utilizados</p>
                  <p className="text-2xl font-bold text-amber-900" data-testid="consent-used">
                    {membership.consentimientos_usados}
                  </p>
                </div>
                <div className={`rounded-xl p-4 ${membership.consentimientos_disponibles <= 0 && membership.cantidad_consentimientos !== -1 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                  <p className={`text-sm mb-1 ${membership.consentimientos_disponibles <= 0 && membership.cantidad_consentimientos !== -1 ? 'text-red-600' : 'text-emerald-600'}`}>
                    Consentimientos Restantes
                  </p>
                  <p className={`text-2xl font-bold ${membership.consentimientos_disponibles <= 0 && membership.cantidad_consentimientos !== -1 ? 'text-red-900' : 'text-emerald-900'}`} data-testid="consent-remaining">
                    {membership.cantidad_consentimientos === -1 ? '∞' : membership.consentimientos_disponibles}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-600 mb-1">Fecha de Renovación</p>
                  <p className="text-xl font-bold text-slate-900" data-testid="renewal-date">
                    {formatDateUTC(membership.fecha_renovacion)}
                  </p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4">
                  <p className="text-sm text-emerald-600 mb-1">Valor de la Membresía</p>
                  <p className="text-2xl font-bold text-emerald-900" data-testid="membership-value">
                    {formatCurrency(membership.valor_membresia, membership.moneda)}
                  </p>
                </div>
              </div>
              
              {/* Payment Status & Action Buttons */}
              <div className="flex flex-col gap-4 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600">Estado de Pago:</span>
                  <span className={`px-4 py-2 rounded-full font-semibold ${
                    membership.estado_pago === 'Vigente' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`} data-testid="payment-status">
                    {membership.estado_pago}
                  </span>
                  {membership.dias_vencidos > 0 && (
                    <span className="text-red-600 text-sm font-medium">
                      ({membership.dias_vencidos} días vencido)
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => handleOpenPaymentModal('renewal')}
                    className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors shadow-lg shadow-emerald-200"
                    data-testid="btn-pagar-membresia"
                  >
                    <CreditCard size={18} />
                    Pagar Membresía
                  </button>
                  <button
                    onClick={handleRenewMembership}
                    disabled={processingRenewal}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
                    data-testid="btn-renovar-membresia"
                  >
                    {processingRenewal ? <Loader2 className="animate-spin" size={18} /> : <RefreshCw size={18} />}
                    Renovar Anticipadamente
                  </button>
                  <button
                    onClick={handleOpenUpgradeModal}
                    className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition-colors"
                    data-testid="btn-upgrade-membresia"
                  >
                    <ArrowUpCircle size={18} />
                    Cambiar Plan
                  </button>
                </div>
              </div>

              {/* Payment History */}
              {showHistory && (
                <div className="mt-6 pt-6 border-t border-slate-200" data-testid="payment-history-section">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Historial de Transacciones</h3>
                  {paymentHistory.length === 0 ? (
                    <p className="text-slate-500 text-sm">No hay transacciones registradas.</p>
                  ) : (
                    <div className="space-y-3">
                      {paymentHistory.map((txn) => (
                        <div key={txn.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`txn-${txn.id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${txn.status === 'paid' ? 'bg-emerald-500' : txn.status === 'processing' ? 'bg-blue-500' : txn.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'}`}></div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{txn.description || 'Pago de membresía'}</p>
                              <p className="text-xs text-slate-500">
                                {txn.gateway === 'bold' ? 'Bold.co' : txn.gateway === 'flow' ? 'Flow.cl' : 'Manual'} 
                                {' '}{txn.created_at ? format(new Date(txn.created_at), 'dd/MM/yyyy HH:mm') : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-slate-900">{formatCurrency(txn.amount, txn.currency)}</span>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${statusColors[txn.status] || 'bg-slate-100 text-slate-600'}`}>
                              {statusLabels[txn.status] || txn.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="text-slate-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Sin Membresía Activa</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Su centro no tiene una membresía activa. Contacte con nosotros para activar su membresía y comenzar a utilizar todas las funciones del sistema.
              </p>
              <button
                onClick={() => handleOpenPaymentModal()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors"
                data-testid="btn-activar-membresia"
              >
                <CreditCard size={20} />
                Contactar para Activar
              </button>
            </div>
          )}
        </div>

        {/* Payment Method Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="payment-modal-overlay" onClick={() => !processingPayment && setShowPaymentModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-900">
                    {paymentAction.type === 'renewal' ? 'Pagar Renovación' : paymentAction.type === 'upgrade' ? 'Pagar Cambio de Plan' : 'Seleccionar Medio de Pago'}
                  </h3>
                  <button
                    onClick={() => !processingPayment && setShowPaymentModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400"
                    data-testid="close-payment-modal"
                  >
                    ×
                  </button>
                </div>
                {membership && (
                  <div className="mt-3 px-4 py-3 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-700">
                      <span className="font-semibold">Membresía:</span> {membership.tipo_membresia}
                    </p>
                    <p className="text-lg font-bold text-amber-900">
                      {formatCurrency(membership.valor_membresia, membership.moneda)}
                    </p>
                  </div>
                )}
              </div>
              <div className="p-6 space-y-3">
                {processingPayment ? (
                  <div className="flex flex-col items-center gap-3 py-8">
                    <Loader2 className="animate-spin text-violet-600" size={40} />
                    <p className="text-slate-600 font-medium">Procesando pago...</p>
                  </div>
                ) : (
                  paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => handleSelectPaymentMethod(method)}
                      className="w-full flex items-center gap-4 p-4 border-2 border-slate-200 hover:border-violet-400 hover:bg-violet-50 rounded-xl transition-all text-left"
                      data-testid={`payment-method-${method.id}`}
                    >
                      <div className={`p-3 rounded-lg ${method.type === 'online' ? 'bg-violet-100' : 'bg-emerald-100'}`}>
                        {method.type === 'online' ? (
                          <CreditCard className="text-violet-600" size={24} />
                        ) : (
                          <MessageCircle className="text-emerald-600" size={24} />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-slate-900">{method.name}</p>
                        <p className="text-sm text-slate-500">{method.description}</p>
                      </div>
                      <ExternalLink className="text-slate-400" size={18} />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Plan Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="upgrade-modal-overlay" onClick={() => !processingUpgrade && setShowUpgradeModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ArrowUpCircle className="text-violet-600" size={24} />
                    <h3 className="text-xl font-bold text-slate-900">Cambiar Plan</h3>
                  </div>
                  <button
                    onClick={() => !processingUpgrade && setShowUpgradeModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400"
                    data-testid="close-upgrade-modal"
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-2">Seleccione el plan al que desea actualizar su membresía</p>
              </div>
              <div className="p-6 space-y-3">
                {processingUpgrade ? (
                  <div className="flex flex-col items-center gap-3 py-8">
                    <Loader2 className="animate-spin text-violet-600" size={40} />
                    <p className="text-slate-600 font-medium">Actualizando plan...</p>
                  </div>
                ) : (
                  availablePlans.map((plan) => (
                    <div
                      key={plan.id}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        plan.is_current 
                          ? 'border-violet-400 bg-violet-50' 
                          : 'border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                      data-testid={`plan-${plan.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-slate-900">{plan.nombre}</h4>
                            {plan.is_current && (
                              <span className="px-2 py-0.5 bg-violet-200 text-violet-700 text-xs font-semibold rounded-full">
                                Plan Actual
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {plan.cantidad_consentimientos === -1 ? 'Consentimientos ilimitados' : `${plan.cantidad_consentimientos} consentimientos/mes`}
                            {plan.codigo_firma_habilitado && (
                              <span className="ml-2 text-emerald-600 font-medium">+ Firma por código</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-900">
                            {formatCurrency(plan.valor, plan.moneda)}
                          </p>
                          <p className="text-xs text-slate-500">/mes</p>
                        </div>
                      </div>
                      {!plan.is_current && (
                        <button
                          onClick={() => handleUpgradePlan(plan.id, plan.nombre)}
                          className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-lg transition-colors text-sm"
                          data-testid={`btn-upgrade-to-${plan.id}`}
                        >
                          <Zap size={16} />
                          Cambiar a {plan.nombre}
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Datos del Centro */}
        {company && (
          <div className="mb-8 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="datos-centro-section">
            <div className="bg-gradient-to-r from-violet-500 to-indigo-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <Building2 className="text-white" size={24} />
                <h2 className="text-xl font-bold text-white">Datos del Centro</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Código de Empresa</p>
                  <p className="font-mono font-bold text-violet-600">{company.codigo_empresa}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Razón Social</p>
                  <p className="font-semibold text-slate-900">{company.razon_social || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Documento</p>
                  <p className="font-mono text-slate-900">{company.numero_documento || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">País</p>
                  <p className="text-slate-900">{company.pais || 'N/A'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-slate-500 mb-1">Dirección</p>
                  <p className="text-slate-900">{company.direccion || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading || membershipLoading ? (
          <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        ) : (
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ${isBlocked ? 'opacity-50' : ''}`}>
            {statCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  data-testid={card.testId}
                  className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg bg-${card.color}-50`}>
                      <Icon className={`text-${card.color}-600`} size={24} />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">{card.value}</h3>
                  <p className="text-sm text-slate-600">{card.title}</p>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Acerca de Konemed</h2>
          <p className="text-slate-600 mb-4">
            Sistema de gestión de consentimientos informados para tu clínica.
            Gestiona, envía y almacena consentimientos de forma digital y segura.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="p-4 bg-violet-50 rounded-lg">
              <h3 className="font-semibold text-violet-900 mb-1">Seguro</h3>
              <p className="text-sm text-violet-700">Firmas electrónicas validadas</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg">
              <h3 className="font-semibold text-emerald-900 mb-1">Rápido</h3>
              <p className="text-sm text-emerald-700">Envío instantáneo por email/WhatsApp</p>
            </div>
            <div className="p-4 bg-cyan-50 rounded-lg">
              <h3 className="font-semibold text-cyan-900 mb-1">Legal</h3>
              <p className="text-sm text-cyan-700">PDFs con QR y serial único</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
